import { BuiSegmentedControl } from "@btnet/blueprint-components/BuiSegmentedControl";

import { useTracking } from "utilities/analytics/analytics";

import type { IBTSegmentedControlProps } from "./BTSegmentedControl.types";

function BTSegmentedControlComponent(props: IBTSegmentedControlProps) {
    const {
        testid,
        children,
        className,
        disabled,
        fullWidth,
        value,
        onValueChange,
        "aria-label": ariaLabel,
    } = props;

    const { trackEvent, Track } = useTracking({ element: "SegmentedControl", uniqueId: testid });

    const handleValueChange = (newValue: string) => {
        if (newValue) {
            trackEvent({ event: "ValueChange", extraInfo: { value: newValue } });
            onValueChange(newValue);
        }
    };

    return (
        <Track>
            <BuiSegmentedControl
                testid={testid}
                aria-label={ariaLabel}
                type="single"
                value={value}
                onValueChange={handleValueChange}
                className={className}
                disabled={disabled}
                fullWidth={fullWidth}
            >
                {children}
            </BuiSegmentedControl>
        </Track>
    );
}

/**
 * BTSegmentedControl — app-level segmented control (single selection).
 *
 * Uses a composition pattern — pass `BTSegmentedControl.Option` children for the
 * recommended styled option, or any custom element for full control.
 *
 * @example
 * <BTSegmentedControl testid="job-picker" value={view} onValueChange={setView}>
 *     <BTSegmentedControl.Option testid="item-jobs" value="jobs">Jobs</BTSegmentedControl.Option>
 *     <BTSegmentedControl.Option testid="item-templates" value="templates">Templates</BTSegmentedControl.Option>
 * </BTSegmentedControl>
 */
export const BTSegmentedControl = Object.assign(BTSegmentedControlComponent, {
    /**
     * BTSegmentedControl.Option — individual option within the segmented control.
     *
     * **Default mode:** renders a styled button with selected/unselected visual states.
     * Pass text, icons, or any ReactNode as children.
     *
     * **Composition mode (`asChild`):** pass a single child element to take full
     * control of rendering. Radix merges toggle behavior (data-state, aria attrs,
     * keyboard nav) onto your element; default button styling is skipped.
     *
     * @param props.testid - Test ID for the option.
     * @param props.value - Unique string value identifying this option within the group.
     * @param props.asChild - When true, renders the child element instead of a button
     *   and skips default styling. The child receives Radix's toggle behavior via props.
     */
    Option: BuiSegmentedControl.Option,
});
